/**
 * @file Contains functions dedicated to rendering table layouts
 * for different item types (Tasks, Patients, Projects, Assets).
 * Includes the generic `renderSingleTable` helper and functions for
 * column resizing, sorting, and filtering interactions within tables.
 */

// --- Imports ---

// State management
import { state, appData, saveViewSettings } from '../../app/state/state.js';
import { applyBoardRuntimePipeline } from './boardRuntime.js';
// Utility functions
import {
    isItemClosed,
} from "../utils/closedState.js";
import {
    findItem,
    getItemDisplayName,
    getNextActionDate,
    getNextDueDate,
} from "../utils/itemLookup.js";
import {
    formatDate,
} from "../utils/dateUtils.js";
import {
    formatCurrency,
    isColorDark,
} from "../utils/displayUtils.js";
import {
    getUsersTasks,
    getTaskQuickCloseStatusConfig,
} from "../utils/taskDisplay.js";
import {
    getPatientProviderDisplayNames,
} from "../utils/providerUtils.js";
import {
    getUserDisplayNameById,
} from "../utils/userDisplay.js";
import {
    escapeHTML,
} from "../utils/contentUtils.js";
import { getBoardTableColumnDefinitions } from './boardTableColumns.js';
import { renderBoardTableHeader, renderBoardTableStateCard, renderGroupedTableSections } from './boardTableView.js';
import { addBoardTableInteractionListeners } from './boardTableInteractions.js';
import {
    renderBoardTableTypeBadges,
    renderQuickAddTaskCell,
    renderTaskQuickCloseCell,
    renderTaskRecurringTableIcon,
} from './boardTableCells.js';
import { applyBrandingVariables } from '../ui/brandingTokens.js';
// Modal/Form functions
import { openParentForm } from '../forms/parentFormModal.js';
// Navigation functions
import { showDetails } from '../../app/routing/navigationController.js';
import { quickCloseTask as saveQuickCloseTask } from '../api/checklists.js';
import { loadMoreClosedItems } from '../api/parentItems.js';

function getTypeConfigForItemType(type) {
    switch (type) {
        case 'patient': return appData?.config?.patientTypes || [];
        case 'project': return appData?.config?.projectTypes || [];
        case 'asset': return appData?.config?.assetTypes || [];
        case 'task': return appData?.config?.taskTypes || [];
        default: return [];
    }
}

function renderTypeBadges(typeIds, type) {
    return renderBoardTableTypeBadges(typeIds, getTypeConfigForItemType(type));
}

function applyBoardTableBranding(board) {
    applyBrandingVariables(board, appData);
}

// --- Main Table Rendering Functions ---

/**
 * Renders the Task view in a table format, grouped by status category.
 * Handles filtering, sorting, column visibility, and adds interaction listeners.
 * @param {HTMLElement} board - The container element for the table(s).
 * @param {Array<object>} tasks - The list of task objects to render (usually filtered for the current user).
 */
export function renderTaskTable(board, tasks) {
    if (!board) { console.error("renderTaskTable: Board element not provided."); return; }
    if (!tasks) { console.error("renderTaskTable: Tasks array not provided."); return; }

    applyBoardTableBranding(board);
    board.className = ''; // Reset class for table view

    const settings = state.viewSettings?.tasks;
    if (!settings) {
        console.error("renderTaskTable: Task view settings not loaded.");
        board.innerHTML = renderBoardTableStateCard({
            title: 'Board unavailable',
            body: 'The table settings could not be loaded.',
            tone: 'error',
        });
        return;
    }
    const filteredTasks = applyBoardRuntimePipeline({
        viewKey: 'tasks',
        itemType: 'task',
        items: tasks,
        taskList: appData.tasks,
    });

    board.innerHTML = renderGroupedTableSections(filteredTasks, {
        viewKey: 'tasks',
        itemType: 'task',
        singularLabel: 'task',
        sourceItems: tasks,
        groupBy: settings.tableGroupBy,
        dateGroupingMode: settings.tableDateGroupingMode,
        activeFilterKeys: [],
        renderTable: renderSingleTable,
    });

    addTableInteractionListeners(board, 'task');
}


/**
 * Renders the Patient view in a table format, grouped by status category.
 * @param {HTMLElement} board - The container element for the table(s).
 */
export function renderPatientTable(board) {
    if (!board) { console.error("renderPatientTable: Board element not provided."); return; }
    applyBoardTableBranding(board);
    board.className = ''; // Reset class

    const settings = state.viewSettings?.patients;
    if (!settings) {
        console.error("renderPatientTable: Patient view settings not loaded.");
        board.innerHTML = renderBoardTableStateCard({
            title: 'Board unavailable',
            body: 'The table settings could not be loaded.',
            tone: 'error',
        });
        return;
    }
    const filteredPatients = applyBoardRuntimePipeline({
        viewKey: 'patients',
        itemType: 'patient',
        items: appData?.patients || [],
        taskList: appData.tasks,
    });

    board.innerHTML = renderGroupedTableSections(filteredPatients, {
        viewKey: 'patients',
        itemType: 'patient',
        singularLabel: 'patient',
        sourceItems: appData?.patients || [],
        groupBy: settings.tableGroupBy,
        dateGroupingMode: settings.tableDateGroupingMode,
        activeFilterKeys: [],
        renderTable: renderSingleTable,
    });

    addTableInteractionListeners(board, 'patient');
}

/**
 * Renders the Project view in a table format, grouped by status category.
 * @param {HTMLElement} board - The container element for the table(s).
 */
export function renderProjectTable(board) {
    if (!board) { console.error("renderProjectTable: Board element not provided."); return; }
    applyBoardTableBranding(board);
    board.className = '';

    const settings = state.viewSettings?.projects;
    if (!settings) {
        console.error("renderProjectTable: Project view settings not loaded.");
        board.innerHTML = renderBoardTableStateCard({
            title: 'Board unavailable',
            body: 'The table settings could not be loaded.',
            tone: 'error',
        });
        return;
    }
    const filteredProjects = applyBoardRuntimePipeline({
        viewKey: 'projects',
        itemType: 'project',
        items: appData?.projects || [],
        taskList: appData.tasks,
    });

    board.innerHTML = renderGroupedTableSections(filteredProjects, {
        viewKey: 'projects',
        itemType: 'project',
        singularLabel: 'project',
        sourceItems: appData?.projects || [],
        groupBy: settings.tableGroupBy,
        dateGroupingMode: settings.tableDateGroupingMode,
        activeFilterKeys: [],
        renderTable: renderSingleTable,
    });

    addTableInteractionListeners(board, 'project');
}

/**
 * Renders the Asset view in a table format, grouped by status category.
 * @param {HTMLElement} board - The container element for the table(s).
 */
export function renderAssetTable(board) {
    if (!board) { console.error("renderAssetTable: Board element not provided."); return; }
    applyBoardTableBranding(board);
    board.className = '';

    const settings = state.viewSettings?.assets;
    if (!settings) {
        console.error("renderAssetTable: Asset view settings not loaded.");
        board.innerHTML = renderBoardTableStateCard({
            title: 'Board unavailable',
            body: 'The table settings could not be loaded.',
            tone: 'error',
        });
        return;
    }
    const filteredAssets = applyBoardRuntimePipeline({
        viewKey: 'assets',
        itemType: 'asset',
        items: appData?.assets || [],
        taskList: appData.tasks,
    });

    board.innerHTML = renderGroupedTableSections(filteredAssets, {
        viewKey: 'assets',
        itemType: 'asset',
        singularLabel: 'asset',
        sourceItems: appData?.assets || [],
        groupBy: settings.tableGroupBy,
        dateGroupingMode: settings.tableDateGroupingMode,
        activeFilterKeys: [],
        renderTable: renderSingleTable,
    });

    addTableInteractionListeners(board, 'asset');
}

// --- Internal Table Helpers ---

/**
 * Generates the HTML for a single data table based on items and type. (Internal helper function)
 * @param {Array<object>} items - The array of data objects to render in the table rows.
 * @param {'patient'|'project'|'asset'|'task'} type - The type of items being rendered.
 * @param {Array<string>} [activeFilterKeys=[]] - Array of keys that are currently being filtered.
 * @returns {string} The complete HTML string for the `<table>` element.
 */
// --- FIX: Added 'activeFilterKeys' parameter ---
function renderSingleTable(items, type, activeFilterKeys = []) {
    const settingsKey = `${type}s`; // e.g., 'patients'
    const settings = state.viewSettings?.[settingsKey];
    if (!settings) {
        console.error(`renderSingleTable: Settings not found for type "${type}".`);
        return '<p class="text-red-500 p-4">Error: Table settings missing.</p>';
    }

    const ALL_COLUMNS = getBoardTableColumnDefinitions(settingsKey);
    const { visibleColumns = [], columnWidths = {} } = settings;
    const showTaskQuickClose = type === 'task' &&
        !!getTaskQuickCloseStatusConfig();

    const tableHeaderHTML = renderBoardTableHeader({
        visibleColumns,
        columnLabels: ALL_COLUMNS,
        columnWidths,
        itemType: type,
        showTaskQuickClose,
    });

    // --- Build Table Body ---
    const prioritiesList = appData?.config?.priorities || [];
    const patientStatusList = appData?.config?.patientStatus || [];
    const projectStatusList = appData?.config?.projectStatus || [];
    const equipmentStatusList = appData?.config?.equipmentStatus || [];
    const taskStatusList = appData?.config?.taskStatus || [];
    const _usersList = appData?.users || [];

    const tableBodyHTML = items.length > 0
        ? `<tbody>
            ${items.map(item => {
            let quickAddBtnHtml = '';
            if (type !== 'task' && !isItemClosed(item)) { // Show add task button only for open parent items
                quickAddBtnHtml = renderQuickAddTaskCell(type, item.id);
            }
            const taskQuickCloseHtml = showTaskQuickClose && !isItemClosed(item) ?
                renderTaskQuickCloseCell(item.id) :
                '';

            return `<tr data-id="${item.id}" class="bg-white border-b hover:bg-gray-50 cursor-pointer">
                    ${visibleColumns.map(key => {
                let content = item[key] ?? ''; // Use nullish coalescing for default
                let cellClass = 'px-6 py-4'; // Default padding

                // --- Type-Specific Cell Content Formatting ---
                try {
                    switch (type) {
                        case 'patient':
                            if (key === 'status') {
                                const status = patientStatusList.find(s => s.id === item.status) || { name: item.status, color: '#ccc' };
                                const textColor = isColorDark(status.color) ? 'text-white' : 'text-gray-800'; // from utils.js
                                content = `<span class="px-2 py-0.5 rounded text-xs font-medium ${textColor}" style="background-color: ${status.color};">${status.name}</span>`;
                            } else if (key === 'priority') {
                                const priority = prioritiesList.find(pr => pr.id === item.priority) || { name: item.priority, color: '#ccc' };
                                const textColor = isColorDark(priority.color) ? 'text-white' : 'text-gray-800'; // from utils.js
                                content = `<span class="px-2 py-0.5 rounded text-xs font-medium ${textColor}" style="background-color: ${priority.color};">${priority.name}</span>`;
                            } else if (key === 'types') {
                                content = renderTypeBadges(item.types, 'patient');
                                cellClass = 'px-6 py-4 align-top';
                            } else if (key === 'case_manager') {
                                content = getUserDisplayNameById(item.case_manager);
                            } else if (key === 'provider') {
                                content = getPatientProviderDisplayNames(item.provider).join(', ');
                            } else if (key === 'case_value') {
                                content = formatCurrency(item.case_value); // from utils.js
                            } else if (key === 'next_action_required') {
                                content = formatDate(getNextActionDate(item.id, appData.tasks)); // from utils.js
                            } else if (key.includes('date') || key.includes('appointment') || key.includes('seen')) {
                                content = formatDate(item[key]); // from utils.js
                            }
                            break;
                        case 'project':
                            if (key === 'status') {
                                const status = projectStatusList.find(s => s.id === item.status) || { name: item.status, color: '#ccc' };
                                const textColor = isColorDark(status.color) ? 'text-white' : 'text-gray-800';
                                content = `<span class="px-2 py-0.5 rounded text-xs font-medium ${textColor}" style="background-color: ${status.color};">${status.name}</span>`;
                            } else if (key === 'priority') {
                                const priority = prioritiesList.find(pr => pr.id === item.priority) || { name: item.priority, color: '#ccc' };
                                const textColor = isColorDark(priority.color) ? 'text-white' : 'text-gray-800';
                                content = `<span class="px-2 py-0.5 rounded text-xs font-medium ${textColor}" style="background-color: ${priority.color};">${priority.name}</span>`;
                            } else if (key === 'types') {
                                content = renderTypeBadges(item.types, 'project');
                                cellClass = 'px-6 py-4 align-top';
                            } else if (key === 'project_manager') {
                                content = Array.isArray(item.project_manager) ? item.project_manager.map(id => getUserDisplayNameById(id) || '?').join(', ') : '';
                            } else if (key === 'budget') {
                                content = formatCurrency(item[key]);
                            } else if (key === 'next_due_date') {
                                content = formatDate(getNextDueDate(item.id, appData.tasks)); // from utils.js
                            } else if (key.includes('date')) {
                                content = formatDate(item[key]); // from utils.js
                            }
                            break;
                        case 'asset':
                            if (key === 'types') {
                                content = renderTypeBadges(item.types, 'asset');
                                cellClass = 'px-6 py-4 align-top';
                            } else if (key === 'equipment_status') {
                                const status = equipmentStatusList.find(s => s.id === item.equipment_status) || { name: item.equipment_status, color: '#ccc' };
                                const textColor = isColorDark(status.color) ? 'text-white' : 'text-gray-800';
                                content = `<span class="px-2 py-0.5 rounded text-xs font-medium ${textColor}" style="background-color: ${status.color};">${status.name}</span>`;
                            } else if (key === 'purchase_price') {
                                content = formatCurrency(item.purchase_price); // from utils.js
                            } else if (key.includes('date') || key.includes('due')) {
                                content = formatDate(item[key]); // from utils.js
                            }
                            break;
                        case 'task':
                            if (key === 'title') {
                                content = `<span class="flex min-w-0 items-center gap-1.5"><span class="min-w-0 truncate">${escapeHTML(item.title || '')}</span>${renderTaskRecurringTableIcon(item)}</span>`;
                            } else
                            if (key === 'types') {
                                content = renderTypeBadges(item.types, 'task');
                                cellClass = 'px-6 py-4 align-top';
                            } else if (key === 'status') {
                                const status = taskStatusList.find(s => s.id === item.status) || { name: item.status, color: '#ccc' };
                                const textColor = isColorDark(status.color) ? 'text-white' : 'text-gray-800';
                                content = `<span class="px-2 py-0.5 rounded text-xs font-medium ${textColor}" style="background-color: ${status.color};">${status.name}</span>`;
                            } else if (key === 'priority') {
                                const priority = prioritiesList.find(pr => pr.id === item.priority) || { name: item.priority, color: '#ccc' };
                                const textColor = isColorDark(priority.color) ? 'text-white' : 'text-gray-800';
                                content = `<span class="px-2 py-0.5 rounded text-xs font-medium ${textColor}" style="background-color: ${priority.color};">${priority.name}</span>`;
                            } else if (key === 'owner') {
                                content = getUserDisplayNameById(item.owner);
                            } else if (key === 'assigned_users') {
                                content = Array.isArray(item.assigned_users) ? item.assigned_users.map(id => getUserDisplayNameById(id) || '?').join(', ') : '';
                            } else if (key === 'due_date') {
                                content = formatDate(item[key]); // from utils.js
                            } else if (key === 'parent') {
                                const parent = item.parentId ? findItem(item.parentId) : null; // from utils.js
                                content = parent ? getItemDisplayName(parent) : ''; // Display parent name if found
                            }
                            break;
                        default:
                            break;
                    }
                } catch (err) {
                    console.error(`Error formatting cell content for type "${type}", key "${key}", item ID "${item.id}":`, err);
                    content = `<span class="text-red-500 text-xs italic">Error</span>`; // Display error in cell
                }
                const titleAttr = (typeof content === 'string' && content.length > 50) ? `title="${content.replace(/<[^>]*>/g, '')}"` : '';
                return `<td class="${cellClass}" ${titleAttr}>${content}</td>`;
            }).join('')}
                    ${quickAddBtnHtml}
                    ${taskQuickCloseHtml}
                </tr>`;
        }).join('')}
        </tbody>`
        : `<tbody>
            <tr><td colspan="${visibleColumns.length + (type !== 'task' ? 1 : 0) + (showTaskQuickClose ? 1 : 0)}" class="text-center py-8 text-gray-500">No ${type}s found${(activeFilterKeys?.length > 0) ? ' matching filters' : ''}.</td></tr>
           </tbody>`;

    return `<table class="w-full min-w-[720px] table-fixed text-left text-sm text-gray-500 md:min-w-0">
                ${tableHeaderHTML}
                ${tableBodyHTML}
            </table>`;
}

function renderTableForView(viewType, boardContainer, taskItems) {
    if (viewType === 'task') {renderTaskTable(boardContainer, taskItems || getUsersTasks());}
    else if (viewType === 'patient') {renderPatientTable(boardContainer);}
    else if (viewType === 'project') {renderProjectTable(boardContainer);}
    else if (viewType === 'asset') {renderAssetTable(boardContainer);}
}

async function quickCloseTask(taskId) {
    await saveQuickCloseTask(taskId);
}

function openTaskForm(item) {
    openParentForm('task', item);
}

function addTableInteractionListeners(boardContainer, viewType) {
    addBoardTableInteractionListeners(boardContainer, {
        viewType,
        saveViewSettings,
        getTaskItems: getUsersTasks,
        renderTableForView,
        openTaskForm,
        openDetails: showDetails,
        quickCloseTask,
        loadMoreClosedItems,
    });
}
